import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import Header from './Header';
import { Outlet } from 'react-router-dom';
import { getAuthToken, removeAuthToken } from '../../services/authService';
import baseTheme from '../../theme/theme';

const Layout: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);

  // Create a theme instance with the current mode
  const theme = React.useMemo(
    () =>
      createTheme({
        ...baseTheme,
        palette: {
          ...baseTheme.palette,
          mode: isDarkMode ? 'dark' : 'light',
          background: {
            default: isDarkMode ? '#121212' : '#f5f5f5',
            paper: isDarkMode ? '#1e1e1e' : '#ffffff',
          },
          text: {
            primary: isDarkMode ? '#ffffff' : '#121212',
            secondary: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          },
        },
      }),
    [isDarkMode],
  );

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      setIsAuthenticated(true);
      // In a real app, you would decode the JWT to get the username
      // For now, we'll just use a placeholder
      setUsername('User');
    }
  }, []);

  const handleLogout = () => {
    removeAuthToken();
    setIsAuthenticated(false);
    setUsername('');
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        display: 'flex',
        minHeight: '100vh',
        background: isDarkMode 
          ? 'radial-gradient(circle at 10% 20%, rgba(0, 40, 70, 1) 0%, rgba(18, 18, 18, 1) 90%)'
          : 'radial-gradient(circle at 10% 20%, rgba(230, 245, 255, 1) 0%, rgba(245, 245, 245, 1) 90%)',
        backgroundAttachment: 'fixed'
      }}>
        <Header 
          isAuthenticated={isAuthenticated} 
          username={username} 
          onLogout={handleLogout}
          toggleTheme={toggleTheme}
          isDarkMode={isDarkMode}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${isAuthenticated ? 250 : 0}px)` },
            ml: { sm: isAuthenticated ? `250px` : 0 },
            mt: '64px',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;